import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DocusealForm } from '@docuseal/react';
import { FileSignatureIcon, XIcon } from 'lucide-react';
import { Button, EmptyState, ErrorNotice, LoadingState, Panel, Pill, SkeletonTable } from '@bcecloud/portal-ui';
import { getToken } from '@/lib/auth';
import { useEventContext } from './useEventContext';
import { CLIENT_CONTRACT_PILL, CLIENT_CONTRACT_STATE, clientContractState, type ContractStatus } from './contract-status';
import { HowItWorks, SIGNING_HELP } from '@/components/HowItWorks';

/** A contract as the myevents endpoint returns it. */
interface MyEventContract {
  id: string;
  name: string;
  contractNumber: string | null;
  status: ContractStatus;
  signable: boolean;
  signingUrl: string | null;
  clientSigned: boolean;
  documents: Array<{ name: string; url: string }>;
}

/** The panel open above the list: signing, or a read-only preview. */
type OpenPanel =
  | { readonly kind: 'sign'; readonly contract: MyEventContract }
  | { readonly kind: 'preview'; readonly contract: MyEventContract };

/**
 * The event's contracts, for the client: a list of published contracts, each
 * with Preview and Sign. Choosing either opens a panel above the list — a
 * read-only PDF for Preview, the signing room for Sign.
 *
 * @returns The contracts tab.
 */
export function EventContractsTab(): React.ReactElement {
  const { event } = useEventContext();
  const [contracts, setContracts] = useState<MyEventContract[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<OpenPanel | null>(null);
  // Deep link from the invite email: ?sign=<contractId> opens the signing
  // panel for that contract as soon as the list loads (once — after that
  // the person drives).
  const [searchParams, setSearchParams] = useSearchParams();
  const [preview, setPreview] = useState<
    { state: 'loading' } | { state: 'ready'; url: string } | { state: 'error' }
  >({ state: 'loading' });

  useEffect(() => {
    const signId = searchParams.get('sign');
    if (!signId || contracts === null) return;
    const target = contracts.find((c) => c.id === signId && c.signingUrl);
    if (target) setOpen({ kind: 'sign', contract: target });
    // Consume the param so closing the panel doesn't reopen it.
    setSearchParams((params) => {
      params.delete('sign');
      return params;
    }, { replace: true });
  }, [contracts, searchParams, setSearchParams]);

  async function load(): Promise<void> {
    if (!event?._id) return;
    try {
      const token = getToken();
      const { data } = await axios.get(`/api/v3/events/${event._id}/my-contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContracts(data.data as MyEventContract[]);
      setError(null);
    } catch {
      setError('We could not load your contracts. Please try again.');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?._id]);

  async function openPreview(contract: MyEventContract): Promise<void> {
    setOpen({ kind: 'preview', contract });
    setPreview({ state: 'loading' });
    try {
      const token = getToken();
      const { data } = await axios.get(
        `/api/v3/events/${event?._id}/my-contracts/${contract.id}/preview`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const url = (data.data as { url: string | null }).url;
      setPreview(url ? { state: 'ready', url } : { state: 'error' });
    } catch {
      setPreview({ state: 'error' });
    }
  }

  if (error) return <ErrorNotice title="Contracts unavailable" message={error} />;
  if (contracts === null) {
    return <SkeletonTable rows={3} columns={['w-[30%]', 'w-[15%]']} label="Loading contracts…" />;
  }
  if (contracts.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<FileSignatureIcon aria-hidden className="size-6" />}
          title="No contracts yet"
          message="When your event contract is ready, it appears here for you to review and sign."
        />
      </Panel>
    );
  }

  return (
    <div className="space-y-4">
      <HowItWorks {...SIGNING_HELP} />
      {open ? (
        <Panel className="p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-portal-body font-bold text-portal-ink">
              {open.kind === 'sign' ? 'Review & sign' : 'Preview'} — {open.contract.name}
            </p>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(null)}
              className="rounded p-1 text-portal-muted hover:bg-portal-bg"
            >
              <XIcon className="size-4" />
            </button>
          </div>

          {open.kind === 'sign' && open.contract.signingUrl ? (
            <DocusealForm
              src={open.contract.signingUrl}
              expand={false}
              onComplete={() => {
                setOpen(null);
                void load();
              }}
            />
          ) : null}

          {open.kind === 'preview' ? (
            preview.state === 'loading' ? (
              <div className="p-8">
                <LoadingState label="Loading preview…" />
              </div>
            ) : preview.state === 'error' ? (
              <div className="p-4">
                <ErrorNotice
                  title="Preview unavailable"
                  message="We couldn't load this contract preview. Please try again."
                />
              </div>
            ) : (
              <iframe
                title="Contract preview"
                src={preview.url}
                className="h-[75vh] w-full border-0 bg-white"
              />
            )
          ) : null}
        </Panel>
      ) : null}

      <Panel className="divide-y divide-portal-line p-0">
        {contracts.map((contract) => {
          const state = clientContractState(contract);
          const pill = CLIENT_CONTRACT_PILL[state];
          return (
          <div key={contract.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate text-portal-body font-bold text-portal-ink">
                  {contract.name}
                </p>
                <Pill tone={pill.tone}>{pill.label}</Pill>
              </div>
              {contract.contractNumber ? (
                <p className="text-portal-small text-portal-muted">#{contract.contractNumber}</p>
              ) : null}
              {state === CLIENT_CONTRACT_STATE.waitingOnBce ? (
                <p className="text-portal-small text-portal-muted">
                  You have signed. Best Corporate Events signs next, and nothing more is needed from you.
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => void openPreview(contract)}>
                Preview
              </Button>
              {contract.signable ? (
                <Button onClick={() => setOpen({ kind: 'sign', contract })}>Sign</Button>
              ) : null}
              {state === CLIENT_CONTRACT_STATE.signed && contract.documents.length > 0 ? (
                <a
                  href={contract.documents[0]?.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-portal-small font-bold text-portal-accent hover:underline"
                >
                  Download
                </a>
              ) : null}
            </div>
          </div>
          );
        })}
      </Panel>
    </div>
  );
}
